import { actions, getTsgToken, jwtDecode } from '@sparky/framework'
import { dispatch, stateRef } from './store'
import { ASC_TIER_1_SUPPORT_ROLE } from '../../common/constants/GlobalConstants'
import type { ReadonlyTsgInstances, Tsg } from '@sparky/framework'
import type { SwitcherStoreState } from './store'

export type Action = {
  type: 'update-props'
  props?: Partial<SwitcherStoreState>
} | {
  type: 'set-tsg'
  tsg_id?: string | undefined
} | {
  type: 'set-open'
  isOpen: boolean
} | {
  type: 'set-instances'
  instances: ReadonlyTsgInstances | undefined
  hasAscRole?: boolean
} | {
  type: 'get-current-portal-display-app-id',
}

/**
 * Set values to the top level of global state using object shallow merge
 * @param props - partial of the global state needs to be merged
 */
export const updateStateProps = (props: Partial<SwitcherStoreState>) => {
  dispatch({ type: 'update-props', props })
}

export const setTsg = (tsg: Tsg | string | undefined) => {
  dispatch({ type: 'set-tsg', tsg_id: typeof tsg === 'string' ? tsg : tsg?.id })
}

export const selectTsg = (tsg: Tsg | string | undefined) => {
  setTsg(tsg || stateRef.current.contextTsg)
}

export const setOpen = (isOpen: boolean) => {
  dispatch({ type: 'set-open', isOpen: Boolean(isOpen) })
}

export const open = () => {
  setOpen(true)
}

export const close = () => {
  setOpen(false)
}

export const setInstances = (instances: ReadonlyTsgInstances | undefined, hasAscRole?: boolean) => {
  dispatch({ type: 'set-instances', instances, hasAscRole })
}

const cachedHasAscRoleMap = new Map<string, boolean>()
export const loadTsgInstancesUsingCache = (tsg: Tsg | undefined) => {
  const { isHub, disabled } = stateRef.current
  if (!tsg?.id || isHub || disabled) {
    return
  }
  if (!stateRef.current.hasAscPortal) {
    actions.loadTsgInstancesUsingCache(tsg.id, setInstances)
    return
  }
  const cachedHasAscRole = cachedHasAscRoleMap.get(tsg.id)
  if (cachedHasAscRole !== undefined) {
    actions.loadTsgInstancesUsingCache(tsg.id, (instances) => {
      setInstances(instances, cachedHasAscRole)
    })
  }
  else {
    setInstances(undefined)
  }
  getTsgToken(tsg.id).then((jwt) => {
    const decoded = jwtDecode(jwt)
    const hasAscRole = Boolean(decoded.access && (Object.values(decoded.access) as string[][]).some((acc) => Array.isArray(acc) && acc.includes(ASC_TIER_1_SUPPORT_ROLE)))
    if (cachedHasAscRole === undefined) {
      actions.loadTsgInstancesUsingCache(tsg.id, (instances) => {
        setInstances(instances, hasAscRole)
      })
    }
    cachedHasAscRoleMap.set(tsg.id, hasAscRole)
  })
}

export const getCurrentPortalDisplayAppId = () => {
  dispatch({ type: 'get-current-portal-display-app-id' })
}
